import { t } from "i18next";
import { makeAutoObservable } from "mobx";
import { onError } from "src/common/onError";
import { selectOnRowClick } from "src/components/tables/selectOnRowClick";
import { EntityFiltersPageStore } from "src/pages/EntityFiltersPage/EntityFiltersPageStore";
import { ZUid } from "src/types/zUid";
import { DefaultTSettingsStore } from "src/pages/EntityFiltersPage/DefaultTSettingsStore";
import { TableSettingsType } from "src/pages/ManagementPage/Obj2Tab/ObjTableSettings/ObjTableSettingsTypes";
import { getLatestEntityTSettings } from "src/pages/EntityFiltersPage/EntityTableStrategy";
import { userPreferenceKeys } from "src/common/userPreferenceKeys";
import { assignEntities } from "./apiAssignEntities";
import { ChildEntitiesInfo } from "../../ChildEntitiesStore";
import { makeChildEntitiesRestrictionFilters } from "../../childEntitiesRestriction";

export class ChildEntsAssignStore {
  constructor(private onFinish?: () => void) {
    makeAutoObservable(this);
  }

  filterStore: EntityFiltersPageStore | null = null;

  setFilterStore(store: EntityFiltersPageStore) {
    this.filterStore = store;
  }

  info: ChildEntitiesInfo | null = null;

  setInfo(assinInfo: ChildEntitiesInfo) {
    this.info = assinInfo;
  }

  loading: boolean = false;

  setLoading(flag: boolean) {
    this.loading = flag;
  }

  open: boolean = false;

  setOpen(flag: boolean) {
    this.open = flag;
  }

  init(info: ChildEntitiesInfo | null) {
    try {
      if (!info) throw Error(t("Missing value", { parameter: "info" }));
      const { objectId, restriction } = info;
      this.setFilterStore(
        new EntityFiltersPageStore({
          objectId,
          initialFilters: makeChildEntitiesRestrictionFilters(restriction),
          selectionSettings: { selectionType: "checkbox" },
          defTSettingsStore: new DefaultTSettingsStore(
            TableSettingsType.childObjectId,
          ),
          save: () => this.saveSafeParams(objectId),
          onRowClick: (row) => {
            const tableStore = this.filterStore?.tableStore;
            if (!tableStore) return;
            selectOnRowClick(row, tableStore);
          },
        }),
      );
      this.setInfo(info);
    } catch (error) {
      onError(error);
    }
  }

  async saveSafeParams(objectId: ZUid) {
    const params = this.filterStore?.tableStore?.params;
    const config = this.filterStore?.tableConfig;
    if (!params || !config) return;

    const filters = { ...(params.filters ?? {}) };
    delete filters.attributeId;
    delete filters.restrictionValues;
    await config.save(
      userPreferenceKeys.enitiesSearchLP(objectId),
      getLatestEntityTSettings({ ...params, filters }),
    );
  }

  onClose() {
    this.setOpen(false);
    this.filterStore?.tableStore?.setSelected([]);
  }

  async save() {
    try {
      const { info, selected } = this;
      if (!info) throw Error(t("Missing value", { parameter: "info" }));
      const { parentEntityId, parentLinkAttributeId } = info;
      if (!parentLinkAttributeId)
        throw Error(t("Missing value", { parameter: "parentLinkAttributeId" }));
      this.setLoading(true);
      await assignEntities(parentLinkAttributeId, parentEntityId, selected);
      this.onClose();
      this.onFinish?.();
    } catch (error) {
      onError(error);
    } finally {
      this.setLoading(false);
    }
  }

  get selected(): ZUid[] {
    return this.filterStore?.tableStore?.selected.map(({ id }) => id) ?? [];
  }

  get assignDisabled(): boolean {
    return !this.selected.length;
  }
}
